import http, { endpoints } from '../http';
import { mapAddonGroupFromDto, mapAlacarteProductFromDto, mapPackageFromDto, mapPropertyTypeFromDto, mapSelectableFeeFromDto, } from './helpers';
const efktClient = http.createClient(endpoints.efkt);
/**
 * Account-scoped: all three prices come from the account's price list, nothing on an order takes
 * part, so none of them need an order to exist. "Branch" is an Account in Efkt — there is no
 * separate branch entity — which is why these routes say `accounts`, and `accountId` is the same id
 * already passed to `OrdersApi.submitOrder`.
 *
 * These used to be order-scoped (`GET /api/order/{orderId}/...`), which is why a draft order had to
 * be created before step 1 could show anything — see `submitOrder`. Same mappers as before, so the
 * two families cannot drift.
 *
 * They also hit CRM live on every call, so they are slow relative to a DB read. Do not poll them.
 */
export default class CatalogueApi {
    /**
     * `grouping` is required here even though the wire treats it as optional: every caller has one by
     * the time it can ask, and the same value has to reach `OrdersApi.submitOrder` or the submit is
     * refused. Making it a parameter rather than an option is what stops the two drifting.
     *
     * The response carries no per-package grouping, so `mapPackageFromDto` is handed the value the
     * read was filtered by. See `IBasicPackageDto`.
     *
     * Returns page one. `IBasicPackagesResponseDto.nextToken` is deliberately not walked — no account
     * has come close to a second page, and CRM applies the filter, so a walk would have to carry the
     * same `grouping` through every page. Add the loop here, with that constraint, when one does.
     */
    static async getBasicPackages(accountId, grouping) {
        const response = await efktClient.get(`/api/accounts/${encodeURIComponent(accountId)}/basic-packages`, { params: { grouping } });
        return response.data.items.map(dto => mapPackageFromDto(dto, grouping));
    }
    /**
     * Same filter as `getBasicPackages`, and the same rule: a product CRM has not grouped comes back
     * under every grouping (AXIT-866).
     */
    static async getAddons(accountId, grouping) {
        const response = await efktClient.get(`/api/accounts/${encodeURIComponent(accountId)}/addons`, { params: { grouping } });
        return (response.data.groups || []).map(mapAddonGroupFromDto);
    }
    static async getSelectableFees(accountId, nextLink) {
        const response = await efktClient.get(`/api/accounts/${encodeURIComponent(accountId)}/selectable-fees`, { params: { nextLink } });
        return response.data.items.map(mapSelectableFeeFromDto);
    }
    /** Not order-scoped — a global lookup list. Filter on `isEnabled` before showing it. */
    static async getPropertyTypes() {
        const response = await efktClient.get('/api/property-types');
        return response.data.items.map(mapPropertyTypeFromDto);
    }
    /**
     * EFKT-MOCK — guessed path, no gateway route exists yet. Mirrors datahub's
     * `PackagesApi.getAdditionalProducts` (`GET /branches/{branchId}/products/alacarte`) contract
     * exactly: same bare-array response, "branch" spelled as Efkt's `accountId`. Powers the Esoft-only
     * "Add extra order line" modal — see `OrdersApi.addOrderLines` for the matching write.
     */
    static async getAdditionalProducts(accountId) {
        const response = await efktClient.get(`/api/accounts/${encodeURIComponent(accountId)}/products/alacarte`);
        return response.data.map(mapAlacarteProductFromDto);
    }
}
